
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

type TokenPair = { accessToken: string; refreshToken: string };

const AT_KEY = 'access_token';               // plaintext (kurzlebig)
const RT_KEY = 'refresh_token';              // Native: SecureStorage key
const RT_WEB_KEY = 'refresh_token_enc';      // Web: localStorage (verschlüsselt)
const WEB_AES_KEY_ID = 'web_aes_key_v1';     // Web: IDB-Schlüsselname

// ---------- Mini IndexedDB KV (Web) ----------
function idbOpen(): Promise<IDBDatabase> {
    return new Promise((res, rej) => {
        const req = indexedDB.open('ammsas-secure', 1);
        req.onupgradeneeded = () => req.result.createObjectStore('kv');
        req.onsuccess = () => res(req.result);
        req.onerror = () => rej(req.error);
    });
}

function toArrayBuffer(u8: Uint8Array): ArrayBuffer {
    return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer;
}

async function idbGet<T>(key: string): Promise<T | null> {
    const db = await idbOpen();
    return new Promise((res, rej) => {
        const tx = db.transaction('kv', 'readonly').objectStore('kv').get(key);
        tx.onsuccess = () => res((tx.result as T) ?? null);
        tx.onerror = () => rej(tx.error);
    });
}
async function idbSet<T>(key: string, val: T): Promise<void> {
    const db = await idbOpen();
    return new Promise((res, rej) => {
        const tx = db.transaction('kv', 'readwrite').objectStore('kv').put(val as any, key);
        tx.onsuccess = () => res();
        tx.onerror = () => rej(tx.error);
    });
}

// ---------- Base64 utils ----------
function b64encode(u8: Uint8Array): string {
    let s = ''; const chunk = 0x8000;
    for (let i = 0; i < u8.length; i += chunk) s += String.fromCharCode(...u8.subarray(i, i + chunk));
    return btoa(s);
}
function b64decode(b64: string): Uint8Array {
    const bin = atob(b64); const u8 = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    return u8;
}

@Injectable({ providedIn: 'root' })
export class TokenStorageService {
    private isNative = Capacitor.getPlatform() !== 'web';
    private webCryptoKey: CryptoKey | null = null; // AES-GCM (web)

    // ---- APP_INITIALIZER ruft das einmalig auf (siehe unten) ----
    async init(): Promise<void> {
        // Migration: falls es noch einen alten Plain-RT gibt, migrieren wir ihn in den sicheren Speicher
        const legacyRt = localStorage.getItem('refresh_token');

        if (this.isNative) {
            if (legacyRt) {
                try { await SecureStoragePlugin.set({ key: RT_KEY, value: legacyRt }); } catch { }
                localStorage.removeItem('refresh_token');
            }
            return;
        }

        // Web: AES-Schlüssel laden/erzeugen
        const existing = await idbGet<string>(WEB_AES_KEY_ID);
        if (existing) {
            const raw = b64decode(existing);
            this.webCryptoKey = await crypto.subtle.importKey(
                'raw', toArrayBuffer(raw), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']
            );
        } else {
            this.webCryptoKey = await crypto.subtle.generateKey(
                { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']
            );
            const raw = new Uint8Array(await crypto.subtle.exportKey('raw', this.webCryptoKey));
            await idbSet(WEB_AES_KEY_ID, b64encode(raw));
        }

        // Migration Web: alten Plain-RT -> verschlüsselt speichern
        if (legacyRt && !localStorage.getItem(RT_WEB_KEY)) {
            const enc = await this.webEncrypt(legacyRt);
            localStorage.setItem(RT_WEB_KEY, enc);
            localStorage.removeItem('refresh_token');
        }
    }

    // ---------- Public API ----------
    get accessToken(): string | null { return localStorage.getItem(AT_KEY); }

    async getRefreshToken(): Promise<string | null> {
        try {
            if (this.isNative) {
                const { value } = await SecureStoragePlugin.get({ key: RT_KEY });
                return value ?? null;
            } else {
                const enc = localStorage.getItem(RT_WEB_KEY);
                if (!enc) return null;
                return await this.webDecrypt(enc);
            }
        } catch {
            return null;
        }
    }

    async setTokens(tokens: TokenPair): Promise<void> {
        localStorage.setItem(AT_KEY, tokens.accessToken);
        if (this.isNative) {
            await SecureStoragePlugin.set({ key: RT_KEY, value: tokens.refreshToken });
        } else {
            const enc = await this.webEncrypt(tokens.refreshToken);
            localStorage.setItem(RT_WEB_KEY, enc);
        }
    }

    setAccessToken(accessToken: string) {
        localStorage.setItem(AT_KEY, accessToken);
    }

    async clear(): Promise<void> {
        localStorage.removeItem(AT_KEY);
        if (this.isNative) {
            try { await SecureStoragePlugin.remove({ key: RT_KEY }); } catch { }
        } else {
            localStorage.removeItem(RT_WEB_KEY);
        }
    }

    // ---------- Web AES-GCM helpers ----------
    private async ensureWebKey(): Promise<CryptoKey> {
        if (this.webCryptoKey) return this.webCryptoKey;
        // Fallback: (sollte nicht passieren wenn init() via APP_INITIALIZER lief)
        const existing = await idbGet<string>(WEB_AES_KEY_ID);
        if (existing) {
            const raw = b64decode(existing);
            this.webCryptoKey = await crypto.subtle.importKey('raw', toArrayBuffer(raw), 'AES-GCM', false, ['encrypt', 'decrypt']);
            return this.webCryptoKey;
        }
        this.webCryptoKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
        const raw = new Uint8Array(await crypto.subtle.exportKey('raw', this.webCryptoKey));
        await idbSet(WEB_AES_KEY_ID, b64encode(raw));
        return this.webCryptoKey;
    }

    private async webEncrypt(plain: string): Promise<string> {
        const key = await this.ensureWebKey();
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const ct = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            key,
            new TextEncoder().encode(plain)
        );
        const out = new Uint8Array(iv.length + (ct as ArrayBuffer).byteLength);
        out.set(iv, 0); out.set(new Uint8Array(ct as ArrayBuffer), iv.length);
        return b64encode(out);
        // Format: base64( iv(12) | ciphertext )
    }

    private async webDecrypt(b64: string): Promise<string> {
        const key = await this.ensureWebKey();
        const data = b64decode(b64);
        const iv = data.slice(0, 12);
        const ct = data.slice(12);
        const pt = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            ct
        );
        return new TextDecoder().decode(pt);
    }

}

// bleibt:
export function getJwtExp(token: string | null): number | null {
    if (!token) return null;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return typeof payload?.exp === 'number' ? payload.exp : null;
    } catch { return null; }
}

// import { Injectable } from '@angular/core';

// type TokenPair = { accessToken: string; refreshToken: string };

// @Injectable({ providedIn: 'root' })
// export class TokenStorageService {
//     private AT = 'access_token';
//     private RT = 'refresh_token';

//     get accessToken(): string | null { return localStorage.getItem(this.AT); }
//     get refreshToken(): string | null { return localStorage.getItem(this.RT); }

//     setTokens(tokens: TokenPair) {
//         localStorage.setItem(this.AT, tokens.accessToken);
//         localStorage.setItem(this.RT, tokens.refreshToken);
//     }

//     setAccessToken(accessToken: string) {
//         localStorage.setItem(this.AT, accessToken);
//     }

//     clear() {
//         localStorage.removeItem(this.AT);
//         localStorage.removeItem(this.RT);
//     }
// }

// export function getJwtExp(token: string | null): number | null {
//     if (!token) return null;
//     try {
//         const payload = JSON.parse(atob(token.split('.')[1]));
//         return typeof payload?.exp === 'number' ? payload.exp : null;
//     } catch { return null; }
// }
